import { PORTAL_PATHS } from '@x9/billing';

/**
 * The pre-auth creation wizards. English paths, like every other portal
 * route — Brazil first governs the copy, not the URL (PRD D13).
 */
export const START_PATHS = {
  PASTA: '/start/pasta',
  ROUTINE: '/start/routine',
} as const;

export type AppRoute =
  | { name: 'usage' }
  | { name: 'pricing' }
  | { name: 'startPasta' }
  | { name: 'startRoutine' }
  | { name: 'signin'; nextPath: string }
  | {
      name: 'checkout';
      interval: 'MONTHLY' | 'YEARLY';
      seatCount?: number;
      volumePacks?: number;
      /** @deprecated Prefer seatCount. */
      seatPacks?: number;
      /** Yearly mid-cycle pack upgrade Pay-now flow. */
      mode?: 'pack-upgrade';
    }
  | { name: 'pending'; subscriptionId: string }
  | { name: 'seats' }
  | { name: 'account' }
  | { name: 'email'; nextPath: string }
  | { name: 'verify-email'; token: string };

/** First path segment of every billing/portal route. `/` is work, not portal. */
export const PORTAL_FIRST_SEGMENTS = new Set([
  'signin',
  'checkout',
  'account',
  'seats',
  'pending',
  'email',
  'verify-email',
  'usage',
  'pricing',
  'start',
  'billing',
  'plan',
  'payment',
  'history',
  'go',
]);

export function isPortalPath(pathname: string): boolean {
  const first = pathname.replace(/^\/+|\/+$/g, '').split('/')[0] || '';
  return PORTAL_FIRST_SEGMENTS.has(first.toLowerCase());
}

/** `/signin?next=` so a desktop work visit can reuse the hero + SMS modal. */
export function signInPathFor(nextPath: string): string {
  const next = nextPath.startsWith('/') ? nextPath : PORTAL_PATHS.HOME;
  return `${PORTAL_PATHS.SIGNIN}?next=${encodeURIComponent(next)}`;
}

/**
 * An allowlist, so anything left out is reachable without an account. The
 * `start` wizards rely on that: they ask their questions first and only then
 * send the SMS, which is the whole point of them.
 */
const AUTH_AREA: AppRoute['name'][] = [
  'usage',
  'seats',
  'account',
  'checkout',
  'email',
];

export function requiresAuth(route: AppRoute): boolean {
  return AUTH_AREA.includes(route.name);
}

/**
 * Work already admits a non-anonymous phone account once Terms are on
 * `users/{uid}`. Portal pages must reuse that session — remounting `App`
 * (home → Equipe / Conta) must not force SMS again.
 *
 * A leftover Auth cookie *without* Terms still has to prove the number
 * this visit so we never open the Terms gate on a stale session.
 */
export function isAdmittedPhoneSession(
  user: { isAnonymous: boolean; phoneNumber: string | null } | null,
  recovery: { ready: boolean; termsAccepted: boolean },
): boolean {
  return (
    !!user
    && !user.isAnonymous
    && !!user.phoneNumber
    && recovery.ready
    && recovery.termsAccepted
  );
}

export function pathForRoute(route: AppRoute): string {
  switch (route.name) {
    case 'usage':
      return PORTAL_PATHS.USAGE;
    case 'pricing':
      return PORTAL_PATHS.PRICING;
    case 'startPasta':
      return START_PATHS.PASTA;
    case 'startRoutine':
      return START_PATHS.ROUTINE;
    case 'signin':
      return `${PORTAL_PATHS.SIGNIN}?next=${encodeURIComponent(route.nextPath)}`;
    case 'checkout': {
      const q = new URLSearchParams();
      if (route.mode === 'pack-upgrade') q.set('mode', 'pack-upgrade');
      if (route.interval === 'YEARLY') q.set('interval', 'yearly');
      if (route.seatCount !== undefined) q.set('seatCount', String(route.seatCount));
      else if (route.seatPacks !== undefined) q.set('seatPacks', String(route.seatPacks));
      if (route.volumePacks !== undefined) q.set('volumePacks', String(route.volumePacks));
      const qs = q.toString();
      return qs ? `${PORTAL_PATHS.CHECKOUT}?${qs}` : PORTAL_PATHS.CHECKOUT;
    }
    case 'pending':
      return `${PORTAL_PATHS.PENDING}?sub=${encodeURIComponent(route.subscriptionId)}`;
    case 'seats':
      return PORTAL_PATHS.SEATS;
    case 'account':
      return PORTAL_PATHS.ACCOUNT;
    case 'email':
      return `${PORTAL_PATHS.EMAIL}?next=${encodeURIComponent(route.nextPath)}`;
    case 'verify-email':
      return `${PORTAL_PATHS.VERIFY_EMAIL}?token=${encodeURIComponent(route.token)}`;
    default:
      return PORTAL_PATHS.USAGE;
  }
}

export function pathToRoute(pathname = window.location.pathname, search = window.location.search): AppRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(search);

  if (path === PORTAL_PATHS.PRICING) {
    return { name: 'pricing' };
  }
  if (path === START_PATHS.PASTA) {
    return { name: 'startPasta' };
  }
  if (path === START_PATHS.ROUTINE) {
    return { name: 'startRoutine' };
  }
  if (path.startsWith(PORTAL_PATHS.CHECKOUT)) {
    const yearly = params.get('interval') === 'yearly';
    const sc = Number(params.get('seatCount'));
    const sp = Number(params.get('seatPacks'));
    const vp = Number(params.get('volumePacks'));
    const packUpgrade = params.get('mode') === 'pack-upgrade';
    return {
      name: 'checkout',
      interval: yearly || packUpgrade ? 'YEARLY' : 'MONTHLY',
      ...(packUpgrade ? { mode: 'pack-upgrade' as const } : {}),
      ...(Number.isFinite(sc) && params.get('seatCount') !== null ? { seatCount: sc } : {}),
      ...(Number.isFinite(sp) && params.get('seatPacks') !== null ? { seatPacks: sp } : {}),
      ...(Number.isFinite(vp) && params.get('volumePacks') !== null ? { volumePacks: vp } : {}),
    };
  }
  if (path.startsWith(PORTAL_PATHS.PENDING)) {
    return { name: 'pending', subscriptionId: params.get('sub') || '' };
  }
  if (path.startsWith(PORTAL_PATHS.SIGNIN)) {
    const next = params.get('next') || PORTAL_PATHS.HOME;
    return { name: 'signin', nextPath: next.startsWith('/') ? next : PORTAL_PATHS.HOME };
  }
  if (path.startsWith(PORTAL_PATHS.VERIFY_EMAIL)) {
    return { name: 'verify-email', token: params.get('token') || '' };
  }
  if (path.startsWith(PORTAL_PATHS.EMAIL)) {
    const next = params.get('next') || PORTAL_PATHS.CHECKOUT;
    return { name: 'email', nextPath: next.startsWith('/') ? next : PORTAL_PATHS.CHECKOUT };
  }
  if (
    path.startsWith(PORTAL_PATHS.BILLING)
    || path.startsWith(PORTAL_PATHS.PLAN)
    || path.startsWith(PORTAL_PATHS.PAYMENT)
    || path.startsWith(PORTAL_PATHS.HISTORY)
  ) {
    return { name: 'account' };
  }
  if (path.startsWith(PORTAL_PATHS.USAGE)) return { name: 'usage' };
  if (path.startsWith(PORTAL_PATHS.SEATS)) return { name: 'seats' };
  if (path.startsWith(PORTAL_PATHS.ACCOUNT)) return { name: 'account' };

  return null;
}

export function setPath(path: string) {
  window.history.pushState({}, '', path);
}

export function replacePath(path: string) {
  window.history.replaceState({}, '', path);
}
